import React, { useState } from 'react';
import { ArrowLeftIcon, UploadIcon, StarIcon, MoreHorizontalIcon } from 'lucide-react';
interface RoomImage {
  id: string;
  url: string;
  caption: string;
  uploadedBy: string;
  uploadDate: string;
  isActive: boolean;
  isFeatured?: boolean;
}
interface RoomAssetsProps {
  roomId: string;
  roomName: string;
  images: RoomImage[];
  onBack: () => void;
  onUpload: (files: FileList) => void;
  onUpdateImage: (imageId: string, updates: Partial<RoomImage>) => void;
  onDeleteImage: (imageId: string) => void;
}
export function RoomAssets({
  roomId,
  roomName,
  images,
  onBack,
  onUpload,
  onUpdateImage,
  onDeleteImage
}: RoomAssetsProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('media');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedImages(images.map(img => img.id));
    } else {
      setSelectedImages([]);
    }
  };
  const handleSelectImage = (imageId: string, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };
  return <div className="space-y-6" data-id="element-3879">
      <div className="flex items-center" data-id="element-3880">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-3881">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-3882" />
          <span data-id="element-3883">Room Assets</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3884">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-3885">
          <nav className="flex" data-id="element-3886">
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-3887">
              Room information
            </button>
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('media')} data-id="element-3888">
              Media assets
            </button>
          </nav>
        </div>
        {/* Media Assets Content */}
        <div className="p-6" data-id="element-3889">
          {/* File Upload Area */}
          <div className={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center justify-center ${dragActive ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-3890">
            <div className="p-3 bg-red-100 rounded-full mb-4" data-id="element-3891">
              <UploadIcon size={24} className="text-red-500" data-id="element-3892" />
            </div>
            <p className="text-gray-600 mb-1" data-id="element-3893">Drag files here</p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-3894">
              Supported formats: PNG, JPG, JPEG
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-3895">Max file size: 10MB</p>
            <label className="cursor-pointer" data-id="element-3896">
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFileChange} multiple data-id="element-3897" />
              <span className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700" data-id="element-3898">
                Upload
              </span>
            </label>
          </div>
          {/* Images Table */}
          <div className="overflow-x-auto" data-id="element-3899">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3900">
              <thead data-id="element-3901">
                <tr data-id="element-3902">
                  <th className="w-12 py-3 pl-4" data-id="element-3903">
                    <input type="checkbox" checked={selectedImages.length === images.length && images.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-3904" />
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3905">
                    Room image
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3906">
                    Caption
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3907">
                    Uploaded by
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3908">
                    Uploaded
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3909">
                    Visibility
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3910">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3911">
                {images.map(image => <tr key={image.id} data-id="element-3912">
                    <td className="py-4 pl-4" data-id="element-3913">
                      <input type="checkbox" checked={selectedImages.includes(image.id)} onChange={e => handleSelectImage(image.id, e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-3914" />
                    </td>
                    <td className="py-4" data-id="element-3915">
                      <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden" data-id="element-3916">
                        <img src={image.url} alt={image.caption || 'Room image'} className="w-full h-full object-cover" data-id="element-3917" />
                      </div>
                    </td>
                    <td className="py-4" data-id="element-3918">
                      <div className="flex items-center" data-id="element-3919">
                        {image.isFeatured && <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-3920" />}
                        <span data-id="element-3921">{image.caption || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-3922">
                      {image.uploadedBy}
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-3923">
                      {image.uploadDate}
                    </td>
                    <td className="py-4" data-id="element-3924">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3925">
                        {image.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4" data-id="element-3926">
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-3927">
                        <MoreHorizontalIcon size={16} data-id="element-3928" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}